import { useEffect, useRef } from 'react'

import AuthHeader from 'src/features/auth/components/auth-header'
import { Button } from 'src/shared/components/ui/buttons/button'
import Portal from 'src/shared/components/utils/portals/portal'
import { cn } from 'src/shared/utils/cn'

import { useAuthState } from '../api/use-auth-state'
import type { AuthDialogViews } from '../store'
import { useAuthDialogStore } from '../store'

import AuthSuccessConsumer from './auth-success-consumer'
import AuthSuccessProfessional from './auth-success-professional'
import ConsumerLoginForm from './consumer/login-form'
import ConsumerSignupForm from './consumer/signup-form'
import ProfessionalLoginForm from './professional/login-form'
import ProfessionalOnboardingForm from './professional/onboarding-form'
import ResetPasswordForm from './professional/reset-password-form'
import ProfessionalSignupForm from './professional/signup-form'

function Header() {
  const { setView, view } = useAuthDialogStore()

  const switchToProfessionalLogin = () => {
    setView('professional-login')
  }

  const switchToProfessionalSignup = () => {
    setView('professional-signup')
  }

  const switchToConsumerLogin = () => {
    setView('consumer-login')
  }

  const switchToConsumerSignup = () => {
    setView('consumer-signup')
  }

  if (view === 'consumer-login') {
    return <AuthHeader headerForText="homebuyers" centerHeader={true} onHeaderClick={switchToProfessionalLogin} />
  }

  if (view === 'consumer-signup') {
    return <AuthHeader headerForText="homebuyers" centerHeader={true} onHeaderClick={switchToProfessionalSignup} />
  }

  if (view === 'professional-login') {
    return <AuthHeader headerForText="professionals" centerHeader={true} onHeaderClick={switchToConsumerLogin} />
  }

  if (view === 'professional-signup') {
    return <AuthHeader headerForText="professionals" centerHeader={true} onHeaderClick={switchToConsumerSignup} />
  }

  if (view === 'consumer-login-success' || view === 'consumer-signup-success') {
    return <AuthHeader headerForText="homebuyers" centerHeader={true} />
  }

  if (view === 'professional-login-success' || view === 'professional-signup-success') {
    return <AuthHeader headerForText="professionals" centerHeader={true} />
  }

  return <AuthHeader headerForText="professionals" centerHeader={true} />
}

function Footer() {
  const { setView, view, step } = useAuthDialogStore()

  const handleSignupOnClick = () => {
    setView('consumer-signup')
  }

  const handleLoginOnClick = () => {
    setView('consumer-login')
  }

  const handleProfessionalLoginOnClick = () => {
    setView('professional-login')
  }

  const handleProfessionalSignupOnClick = () => {
    setView('professional-signup')
  }

  if (view === 'consumer-login') {
    return (
      <div className={cn('mt-4 text-center text-sm text-primary', step === 1 && 'hidden')}>
        Don&apos;t have an account?{' '}
        <span className="font-semibold underline hover:cursor-pointer" onClick={handleSignupOnClick}>
          Sign-up
        </span>
      </div>
    )
  }

  if (view === 'consumer-signup') {
    return (
      <div className={cn('mt-4 text-center text-sm text-primary', step === 1 && 'hidden')}>
        I already have an account!{' '}
        <span className="font-semibold underline hover:cursor-pointer" onClick={handleLoginOnClick}>
          Login
        </span>
      </div>
    )
  }

  if (view === 'professional-signup') {
    return (
      <div className={cn('mt-4 text-center text-sm text-primary', step === 1 && 'hidden')}>
        I already have an account!{' '}
        <span className="font-semibold underline hover:cursor-pointer" onClick={handleProfessionalLoginOnClick}>
          Login
        </span>
      </div>
    )
  }

  if (view === 'professional-login') {
    return (
      <div className={cn('mt-4 text-center text-sm text-primary', step === 1 && 'hidden')}>
        Don&apos;t have an account?{' '}
        <span className="font-semibold underline hover:cursor-pointer" onClick={handleProfessionalSignupOnClick}>
          Sign-up
        </span>
      </div>
    )
  }

  return null
}

interface AuthMainDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  initialView?: AuthDialogViews
}

interface AuthSuccessViewConsumerProps {
  setOpen: (open: boolean) => void
  title: string
  description?: string
}

const AuthSuccessViewConsumer = ({ setOpen, title, description }: AuthSuccessViewConsumerProps) => {
  const handleClose = () => {
    setOpen(false)
  }

  // Auto close after 2 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      handleClose()
    }, 2000)

    return () => clearTimeout(timeout)
  }, [handleClose])

  return (
    <AuthSuccessConsumer onClose={handleClose} title={title} description={description || ''} className="pt-[20px]" />
  )
}

interface AuthSuccessViewProfessionalProps {
  title: string
  description?: string
}

const AuthSuccessViewProfessional = ({ title, description }: AuthSuccessViewProfessionalProps) => {
  return <AuthSuccessProfessional title={title} description={description || ''} className="pt-[20px]" />
}

const AuthMainDialog = ({ open, setOpen, initialView = 'consumer-login' }: AuthMainDialogProps) => {
  const { view, setView, setStep, getCurrentStepName, reset } = useAuthDialogStore()

  const { needsOnboarding, isSignedIn } = useAuthState()
  const justSignedUp = useRef(false)

  useEffect(() => {
    if (isSignedIn || needsOnboarding) {
      if (needsOnboarding && isSignedIn) {
        setView('professional-onboarding')
      } else if (isSignedIn) {
        if (justSignedUp.current === true) {
          setView('professional-signup-success')
        } else {
          setView('professional-login-success')
        }
      }
    } else {
      setView(initialView)
    }
  }, [isSignedIn, needsOnboarding, setView, initialView])

  useEffect(() => {
    if (open) {
      reset()
    }
  }, [open, reset])

  const handleForgotPassword = () => {
    setView('reset-password')
  }

  const handleForgotPasswordSuccess = () => {
    setView('professional-login')
  }

  const handleConsumerLoginSuccess = () => {
    setView('consumer-login-success')
  }

  const handleConsumerSignupSuccess = () => {
    setView('consumer-signup-success')
  }

  const handleProfessionalSignupSuccess = () => {
    justSignedUp.current = true
  }

  const handleProfessionalLoginSuccess = () => {
    justSignedUp.current = false
  }

  const showImAProfessionalOuterButton = view === 'consumer-login' || view === 'consumer-signup'

  return (
    <>
      <Portal>
        <div
          className={cn(
            'fixed inset-0 bottom-0 left-0 right-0 top-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/50 transition-opacity duration-300 lg:flex-row lg:gap-8',
            open ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <div
            className={cn(
              'z-[51] flex max-h-[90vh] w-full max-w-[440px] flex-col items-center gap-5 overflow-y-auto py-8',
              view === 'professional-onboarding' && getCurrentStepName() === 'profession' && 'max-w-[772px]',
            )}
          >
            {showImAProfessionalOuterButton && (
              <Button
                variant="default"
                className="h-[50px] w-full max-w-[300px] rounded-full border-none bg-white text-sm text-primary"
                onClick={() => setView('professional-login')}
              >
                I&apos;m a professional <span className="ml-[6px] font-semibold underline">Join now!</span>
              </Button>
            )}
            <div className="relative w-full rounded-[20px] bg-white px-11 pb-9 pt-7">
              <Header />
              {view === 'consumer-login' ? (
                <ConsumerLoginForm
                  onSuccess={() => handleConsumerLoginSuccess()}
                  variant="version2"
                  onStepChange={(step) => setStep(step === 'email' ? 0 : 1)}
                />
              ) : view === 'consumer-signup' ? (
                <ConsumerSignupForm
                  onSuccess={() => handleConsumerSignupSuccess()}
                  variant="version2"
                  onStepChange={(step) => setStep(step === 'signup' ? 0 : 1)}
                />
              ) : view === 'consumer-login-success' || view === 'consumer-signup-success' ? (
                <AuthSuccessViewConsumer
                  setOpen={setOpen}
                  title={view === 'consumer-login-success' ? "You're logged in" : 'Account created'}
                  description={view === 'consumer-login-success' ? 'Welcome back!' : 'Welcome to Conrad!'}
                />
              ) : view === 'professional-login' ? (
                <ProfessionalLoginForm
                  onSuccess={handleProfessionalLoginSuccess}
                  handleForgotPassword={handleForgotPassword}
                />
              ) : view === 'professional-signup' ? (
                <ProfessionalSignupForm
                  onSuccess={handleProfessionalSignupSuccess}
                  onStepChange={(step) => setStep(step === 'signup' ? 0 : 1)}
                />
              ) : view === 'professional-onboarding' ? (
                <ProfessionalOnboardingForm onSuccess={() => {}} setStep={setStep} />
              ) : view === 'professional-login-success' || view === 'professional-signup-success' ? (
                <AuthSuccessViewProfessional
                  title={view === 'professional-login-success' ? 'Login successful' : 'Login successful'}
                  description={
                    view === 'professional-login-success'
                      ? 'Conrad is only available on the App Store for now.'
                      : 'Conrad is only available on the App Store for now.'
                  }
                />
              ) : view === 'reset-password' ? (
                <ResetPasswordForm
                  onSuccess={handleForgotPasswordSuccess}
                  handleBackToLogin={() => setView('professional-login')}
                />
              ) : null}
              <Footer />
            </div>
          </div>
          <div className="fixed inset-0 z-[48]" onClick={() => setOpen(false)} />
        </div>
      </Portal>
    </>
  )
}

export default AuthMainDialog
